import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FaEnvelope } from 'react-icons/fa';
import { computeBranding } from '../utils/useAgentDetails';
import { handleTokenAndPostRequest } from '../utils/utils';

function ShareModal({ agentDetails, show, onHide, propertyId, propertySlug, addressText, agentSlug, onShareSuccess, onShareError, isSendingShare: externalSending, userEmail }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  const { accentColor, buttonTextColor, isLight } = computeBranding(agentDetails) || {};

  const handleSendShare = async () => {
    setError('');
    const emailTrimmed = recipientEmail.trim().toLowerCase();
    if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsSending(true);
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/share-property-email`,
        {
          recipient_email: emailTrimmed,
          address_text: addressText,
          agent_slug: agentSlug,
          property_id: propertyId,
          property_slug: propertySlug,
        }
      );
      if (response.data.status === 'success') {
        setRecipientEmail('');
        if (onShareSuccess) onShareSuccess();
      } else {
        setError(response.data.message || 'Failed to send. Please try again.');
        if (onShareError) onShareError(response.data.message);
      }
    } catch (err) {
      console.error('Share error:', err);
      setError('Failed to send. Please try again.');
      if (onShareError) onShareError();
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setRecipientEmail('');
    setError('');
    onHide();
  };

  return (
    <Modal fullscreen="xl-down" style={{ zIndex: 9999 }} show={show} onHide={handleClose}>
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>Share Property Report</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '16px 24px' }}>
        <p style={{ fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6, marginBottom: 16 }}>
          Enter an email address to share this property report. They'll receive an email with a direct link.
        </p>
        {userEmail && (
          <p style={{ fontSize: '0.82rem', color: '#9ca3af', lineHeight: 1.5, marginBottom: 16, fontStyle: 'italic' }}>
            The recipient will be told this message is from <strong style={{ color: '#6b7280' }}>{userEmail}</strong>.
          </p>
        )}
        <Form.Group className="mb-3">
          <Form.Control
            type="email"
            placeholder="Enter email address"
            value={recipientEmail}
            onChange={(e) => { setRecipientEmail(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendShare(); } }}
            isInvalid={!!error}
            style={{ borderRadius: 10, padding: '12px 14px', fontSize: '0.95rem' }}
          />
          {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
        </Form.Group>
        <Button
          onClick={handleSendShare}
          disabled={isSending || !recipientEmail.trim()}
          style={{
            width: '100%',
            backgroundColor: accentColor,
            borderColor: isLight ? '#222' : accentColor,
            color: buttonTextColor,
            borderRadius: 12,
            padding: '13px 0',
            fontWeight: 600,
            fontSize: '0.95rem',
            boxShadow: `0 4px 16px ${accentColor}30`,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          {isSending ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" style={{ marginRight: '8px' }} />
              Sending...
            </>
          ) : (
            <>
              <FaEnvelope style={{ marginRight: '8px' }} />
              Send
            </>
          )}
        </Button>
      </Modal.Body>
    </Modal>
  );
}

export default ShareModal;